import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { fabric } from 'fabric';
import { Object as FabricObject } from 'fabric/fabric-impl';
import React, { PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import type { MessageDescriptor } from 'shared/i18n/i18n';
import history from 'modules/overlay-canvas/addons/history';
import UDText from 'ud-ui/components/text';
import {
  ALL_KIT_TYPES,
  AUTO_KIT_TYPE,
  KIT_ELEMENT_LABELS,
  KIT_TYPE_LABELS,
  KitElement,
  KitTypeSetting,
  resolveContrastColor,
  resolveTeamColor,
  TeamColorFill,
} from 'shared/kit';
import { applyFillSource, DEFAULT_TEAM_COLOR_FILL, readTeamColorFill } from 'modules/template-editor/domain/utils/apply-fill-source';
import { buildLinearGradient, defaultLinearGradient, firstStopColor, isGradientFill } from 'shared/fabric';
import { FAKE_TEAM_KITS } from 'modules/template-editor/domain/utils/replace-fake-data';
import Checkbox from 'modules/template-editor/ui/components/elements/checkbox';
import Selector from 'modules/template-editor/ui/components/elements/selector';
import { Item, SubItem } from '../styles';
import { useEditorManager } from 'modules/template-editor/ui/context';

interface IProps {
  object: FabricObject;
  /** Подпись секции: у фигуры это «Заливка / фон», у текста — «Цвет шрифта». */
  title: string;
  /** Текст умеет краситься контрастно к цвету команды — фигуре такой режим не нужен. */
  allowContrast?: boolean;
  /** Третье значение источника — «Градиент», его правка — в `gradient`. У текста градиента нет. */
  allowGradient?: boolean;
  /** Секция правки градиента (угол и точки), показывается при источнике «Градиент». */
  gradient?: ReactNode;
  /** Источник сменился и `object.fill` переписан — инспектор перечитывает свой цвет. */
  onChangeSource?: () => void;
}

type FillSource = 'color' | 'gradient' | 'team';

/**
 * Секция «Заливка»: свой цвет (в `children` — обычный ColorPicker), градиент (в `gradient`) или цвет формы команды.
 * Источник один: цвет команды на оверлее перекрашивает объект сплошным поверх любого `fill`, поэтому с градиентом
 * он не совмещается.
 * Выбор пишется в `object.data.fill`; на канве объект сразу перекрашивается фейковыми
 * комплектами, чтобы результат был виден в редакторе, а не только на трансляции.
 */
function TeamColorSource(props: PropsWithChildren<IProps>) {
  const { object, title, allowContrast, allowGradient, gradient, onChangeSource, children } = props;
  const manager = useEditorManager();
  const { t } = useLingui();
  const kitTypeOptions = useMemo(() => buildKitTypeOptions(t), [t]);
  const elementOptions = useMemo(() => buildElementOptions(t), [t]);
  const sourceOptions = useMemo(
    () => translateLabels(allowGradient ? SOURCE_OPTIONS : SOURCE_OPTIONS.filter((option) => option.value !== 'gradient'), t),
    [allowGradient, t],
  );
  const teamOptions = useMemo(() => translateLabels(TEAM_OPTIONS, t), [t]);
  const colorOptions = useMemo(() => translateLabels(COLOR_OPTIONS, t), [t]);
  const [fill, setFill] = useState<TeamColorFill | undefined>(() => readTeamColorFill(object.data));
  const [isGradient, setIsGradient] = useState(() => isGradientFill(object.fill));
  const source: FillSource = fill ? 'team' : allowGradient && isGradient ? 'gradient' : 'color';

  useEffect(() => {
    setFill(readTeamColorFill(object.data));
    setIsGradient(isGradientFill(object.fill));
  }, [object]);

  const update = useCallback(
    (next?: TeamColorFill) => {
      object.data = applyFillSource(object.data, next);
      setFill(next);

      if (next) {
        const kits = next.teamIndex === 1 ? FAKE_TEAM_KITS.second : FAKE_TEAM_KITS.first;
        const color = resolveTeamColor(kits, {
          teamIndex: next.teamIndex,
          kitType: next.kitType,
          element: next.element,
          colorIndex: next.colorIndex,
        });
        object.set('fill', next.mode === 'contrast' ? resolveContrastColor(color) : color);
      }

      setIsGradient(isGradientFill(object.fill));
      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  const onChangeSourceOption = (next: FillSource) => {
    if (next === source) return;
    if (next === 'team') {
      update(fill ?? DEFAULT_TEAM_COLOR_FILL);
    } else if (next === 'gradient') {
      const color = isGradientFill(object.fill) ? undefined : object.fill?.toString();
      const model = defaultLinearGradient('#' + new fabric.Color(color ?? '#FFFFFF').toHexa());
      object.set('fill', buildLinearGradient(model, object.width ?? 0, object.height ?? 0));
      update(undefined);
    } else {
      const color = firstStopColor(object.fill);
      if (color) object.set('fill', color);
      update(undefined);
    }
    onChangeSource?.();
  };

  const onFocusField = useCallback(() => {
    manager?.render();
    history.saveState();
  }, [manager]);

  return (
    <Item>
      <span>{title}</span>
      <Selector
        onFocus={onFocusField}
        onChange={(event) => {
          if (event) onChangeSourceOption(event.value as FillSource);
        }}
        options={sourceOptions}
        value={sourceOptions.find((option) => option.value === source)}
      />
      {source === 'color' && children}
      {source === 'gradient' && gradient}
      {fill && (
        <>
          <UDText type={'caption-1'}>{t`Команда`}</UDText>
          <Selector
            onFocus={onFocusField}
            onChange={(event) => {
              update({ ...fill, teamIndex: event?.value === 1 ? 1 : 0 });
            }}
            options={teamOptions}
            value={teamOptions.find((option) => option.value === fill.teamIndex)}
          />
          <UDText type={'caption-1'}>{t`Комплект`}</UDText>
          <Selector
            onFocus={onFocusField}
            onChange={(event) => {
              update({ ...fill, kitType: event?.value as KitTypeSetting });
            }}
            options={kitTypeOptions}
            value={kitTypeOptions.find((option) => option.value === (fill.kitType ?? AUTO_KIT_TYPE))}
          />
          <UDText type={'caption-1'}>{t`Элемент формы`}</UDText>
          <Selector
            onFocus={onFocusField}
            onChange={(event) => {
              update({ ...fill, element: event?.value as KitElement });
            }}
            options={elementOptions}
            value={elementOptions.find((option) => option.value === (fill.element ?? KitElement.SHIRT))}
          />
          <UDText type={'caption-1'}>{t`Цвет`}</UDText>
          <Selector
            onFocus={onFocusField}
            onChange={(event) => {
              update({ ...fill, colorIndex: event?.value === 1 ? 1 : 0 });
            }}
            options={colorOptions}
            value={colorOptions.find((option) => option.value === (fill.colorIndex ?? 0))}
          />
          {allowContrast && (
            <SubItem style={{ justifyContent: 'space-between' }}>
              <span>{t`Контрастный к цвету команды`}</span>
              <Checkbox
                checked={fill.mode === 'contrast'}
                onChange={() => {
                  update({ ...fill, mode: fill.mode === 'contrast' ? 'fill' : 'contrast' });
                }}
              />
            </SubItem>
          )}
        </>
      )}
    </Item>
  );
}

export default TeamColorSource;

const translateLabels = <T,>(options: { label: MessageDescriptor; value: T }[], t: (descriptor: MessageDescriptor) => string) =>
  options.map((option) => ({ label: t(option.label), value: option.value }));

const SOURCE_OPTIONS: { label: MessageDescriptor; value: FillSource }[] = [
  { label: msg`Свой цвет`, value: 'color' },
  { label: msg`Градиент`, value: 'gradient' },
  { label: msg`Цвет команды`, value: 'team' },
];

const TEAM_OPTIONS = [
  { label: msg`Команда №1`, value: 0 },
  { label: msg`Команда №2`, value: 1 },
];

/** Подписи переводятся в рендере: `t` приходит из компонента, на уровне модуля язык ещё не известен. */
const buildKitTypeOptions = (t: (descriptor: MessageDescriptor) => string) => [
  { label: t(msg`Авто (по стороне)`), value: AUTO_KIT_TYPE as KitTypeSetting },
  ...ALL_KIT_TYPES.map((type) => ({ label: t(KIT_TYPE_LABELS[type]), value: type as KitTypeSetting })),
];

const buildElementOptions = (t: (descriptor: MessageDescriptor) => string) =>
  Object.values(KitElement).map((element) => ({ label: t(KIT_ELEMENT_LABELS[element]), value: element }));

const COLOR_OPTIONS = [
  { label: msg`Основной`, value: 0 },
  { label: msg`Цвет узора`, value: 1 },
];
